import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiClient } from '../services/api';
import { hasAuthData } from '../utils/authHeaders';

export interface UserAchievement {
  achievementId: string;
  unlockedAt: Date;
  claimed?: boolean; // Активировано ли достижение (получены ли токены)
  claimedAt?: Date; // Когда было активировано
  progress?: number; // Для достижений с градацией
  currentTier?: number; // Текущий уровень достижения (для градаций I/II/III/IV)
}

interface AchievementsState {
  unlockedAchievements: UserAchievement[];
  achievementProgress: Record<string, number>; // Прогресс для достижений с градацией
  isLoading: boolean;
  error: string | null;
  _lastUpdate: number;
  lastReadTimestamp: number; // Время последнего посещения раздела достижений

  setUnlockedAchievements: (achievements: UserAchievement[]) => void;
  unlockAchievement: (achievementId: string, progress?: number, tier?: number) => void;
  updateProgress: (achievementId: string, progress: number) => void;
  isAchievementUnlocked: (achievementId: string) => boolean;
  isAchievementClaimed: (achievementId: string) => boolean;
  getAchievementProgress: (achievementId: string) => number;
  fetchAchievements: (forceRefresh?: boolean) => Promise<void>;
  claimAchievement: (achievementId: string, reward: number) => Promise<{ success: boolean; message: string; newBalance?: number; requiresPaidSubscription?: boolean }>;
  hasUnreadAchievements: () => boolean;
  markAchievementsAsRead: () => void;
}

// Кэш для предотвращения частых запросов
let lastFetchTime = 0;
const CACHE_DURATION = 10000; // 10 секунд

export const useAchievementsStore = create<AchievementsState>()(
  persist(
    (set, get) => ({
      unlockedAchievements: [],
      achievementProgress: {},
      isLoading: false,
      error: null,
      _lastUpdate: 0,
      lastReadTimestamp: 0,

      setUnlockedAchievements: (achievements: UserAchievement[]) =>
        set({ 
          unlockedAchievements: achievements,
          _lastUpdate: Date.now() 
        }),

      unlockAchievement: (achievementId: string, progress?: number, tier?: number) =>
        set((state) => {
          // Проверяем, не разблокировано ли уже
          const alreadyUnlocked = state.unlockedAchievements.some(
            (a) => a.achievementId === achievementId
          );
          
          if (alreadyUnlocked) {
            return state;
          }

          return {
            unlockedAchievements: [
              ...state.unlockedAchievements,
              {
                achievementId,
                unlockedAt: new Date(),
                progress,
                currentTier: tier
              }
            ],
            _lastUpdate: Date.now()
          };
        }),

      updateProgress: (achievementId: string, progress: number) =>
        set((state) => ({
          achievementProgress: {
            ...state.achievementProgress,
            [achievementId]: progress
          },
          _lastUpdate: Date.now()
        })),

      isAchievementUnlocked: (achievementId: string) => {
        const state = get();
        return state.unlockedAchievements.some(
          (a) => a.achievementId === achievementId
        );
      },

      isAchievementClaimed: (achievementId: string) => {
        const state = get();
        const achievement = state.unlockedAchievements.find(
          (a) => a.achievementId === achievementId
        );
        return achievement?.claimed || false;
      },

      getAchievementProgress: (achievementId: string) => {
        const state = get();
        return state.achievementProgress[achievementId] || 0;
      },

      fetchAchievements: async (forceRefresh = false) => {
        const now = Date.now();
        const timeSinceLastFetch = now - lastFetchTime;
        
        if (!forceRefresh && timeSinceLastFetch < CACHE_DURATION && get().unlockedAchievements.length > 0) {
          return;
        }

        set({ isLoading: true, error: null });
        
        try {
          if (!hasAuthData()) {
            throw new Error('Not authenticated');
          }

          const data = await apiClient.achievements.getAchievements(forceRefresh);

          if (data.success && data.achievements) {
            lastFetchTime = now;
            
            const formattedAchievements = data.achievements.map((a: any) => ({
              ...a,
              unlockedAt: new Date(a.unlockedAt),
              claimedAt: a.claimedAt ? new Date(a.claimedAt) : undefined
            }));
            
            set({ 
              unlockedAchievements: formattedAchievements,
              achievementProgress: data.progress || {},
              isLoading: false,
              error: null,
              _lastUpdate: Date.now()
            });
          } else {
            throw new Error(data.error || 'Failed to fetch achievements');
          }
        } catch (error: any) {
          console.error('❌ Error fetching achievements:', error);
          set({
            isLoading: false,
            error: error.response?.data?.message || error.message || 'Failed to fetch achievements'
          });
        }
      },

      claimAchievement: async (achievementId: string, reward: number) => {
        try {
          if (!hasAuthData()) {
            throw new Error('Not authenticated');
          }

          const data = await apiClient.achievements.claimAchievement(achievementId, reward);

          if (data.success) {
            // Обновляем достижение как активированное
            set((state) => ({
              unlockedAchievements: state.unlockedAchievements.map((a) =>
                a.achievementId === achievementId
                  ? { ...a, claimed: true, claimedAt: new Date() }
                  : a
              ),
              _lastUpdate: Date.now()
            }));

            return {
              success: true,
              message: data.message,
              newBalance: data.newBalance
            };
          } else {
            return {
              success: false,
              message: data.message || 'Failed to claim achievement'
            };
          }
        } catch (error: any) {
          console.error('❌ Error claiming achievement:', error);
          return {
            success: false,
            message: error.response?.data?.message || error.message || 'Failed to claim achievement',
            requiresPaidSubscription: error.response?.data?.requiresPaidSubscription || false
          };
        }
      },

      hasUnreadAchievements: () => {
        const state = get();
        const lastRead = state.lastReadTimestamp;
        
        // Проверяем есть ли достижения, разблокированные после последнего прочтения
        const hasUnread = state.unlockedAchievements.some((achievement) => {
          const unlockedTime = achievement.unlockedAt instanceof Date 
            ? achievement.unlockedAt.getTime()
            : new Date(achievement.unlockedAt).getTime();
          return unlockedTime > lastRead;
        });
        
        return hasUnread;
      },

      markAchievementsAsRead: () => {
        const now = Date.now();
        set({ lastReadTimestamp: now });
      },
    }),
    {
      name: 'achievements-storage',
      partialize: (state) => ({
        unlockedAchievements: state.unlockedAchievements,
        achievementProgress: state.achievementProgress,
        _lastUpdate: state._lastUpdate,
        lastReadTimestamp: state.lastReadTimestamp
      }),
    }
  )
);

